import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { clinicDayFromIso, formatDate } from '@/shared/lib/date';

import type { AppError } from './AppError';

/** A clinic day as the backend puts it in an exception context: `YYYY-MM-DD`. */
const CLINIC_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Turns an `AppError` into the sentence a person reads.
 *
 * Three sources, in order of how much they know about *this* failure:
 *
 * 1. the local `errors` catalogue, keyed by the machine code — the only one
 *    that can carry a next step written for this screen;
 * 2. the message the API already sent, translated server-side against the same
 *    code and the same `Accept-Language`, with its placeholders filled in;
 * 3. «مشکلی پیش آمد. لطفاً دوباره تلاش کنید.»
 *
 * Step 2 is the point. The backend catalogue is much larger than this one, and
 * every code missing here used to fall straight to step 3 — so «تعرفهٔ ویزیت
 * ثبت نشده» reached the patient as *try again*, advice that could never work
 * and hid the real reason. A refusal the server explained must not be replaced
 * by a guess.
 */
export function useErrorText(): (
  error: AppError,
  vars?: Readonly<Record<string, unknown>>,
) => string {
  const { t, i18n } = useTranslation(['errors', 'common']);
  const locale = i18n.language === 'en' ? 'en' : 'fa';

  return useCallback(
    (error: AppError, vars: Readonly<Record<string, unknown>> = {}): string => {
      // AppError falls back to the code as its message, which is not a sentence
      const serverMessage = error.message === error.code ? '' : error.message.trim();

      return t(error.code, {
        ns: 'errors',
        ...localizeDays(error.context, locale),
        ...vars,
        defaultValue: serverMessage === '' ? t('common:errors.generic') : serverMessage,
      });
    },
    [t, locale],
  );
}

/**
 * Turn every clinic day in an exception context into the reader's calendar.
 *
 * A domain exception carries machine-readable values — «این نوبت برای
 * 2026-08-20 است» is what the raw context produces, a Gregorian date in Latin
 * digits dropped into the middle of a Persian sentence. Jalali happens at the
 * presentation edge (§9), and every refusal that names a day passes through
 * here, so this is the edge for all of them rather than each screen
 * remembering separately.
 *
 * Only `YYYY-MM-DD` values are touched. An id, a count and a status are left
 * exactly as the server sent them.
 */
function localizeDays(
  context: Readonly<Record<string, unknown>> | undefined,
  locale: string,
): Record<string, unknown> {
  if (context === undefined) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(context).map(([key, value]) => [
      key,
      typeof value === 'string' && CLINIC_DAY.test(value)
        ? formatDate(clinicDayFromIso(value), locale)
        : value,
    ]),
  );
}
